import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import { ToastContainer, useToast } from '../../components/ui/Toast';
import Icon from '../../components/AppIcon';
import { getPaymentByIntentId } from '../../services/paymentService';

const PaymentConfirmation = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toasts, addToast, removeToast } = useToast();

  const paymentIntentId = searchParams?.get('paymentIntentId') || null;
  const projectId = searchParams?.get('project') || 'PROJ-DEMO1';
  const vendorName = searchParams?.get('vendor') || 'Amazon';

  const [showConfetti, setShowConfetti] = useState(true);
  const [pageLoading, setPageLoading] = useState(true);
  const [paymentRecord, setPaymentRecord] = useState(null);

  const getVendorData = (name) => {
    const vendors = {
      'Amazon': {
        name: 'Amazon',
        logo: 'https://images.unsplash.com/photo-1614576205864-be9d97375f1d',
        logoAlt: 'Apple MacBook Pro 14-inch with M3 Pro chip in Space Gray, sleek aluminum design',
        contact: { email: 'support@amazon.com', phone: '(888) 280-4331', representative: 'Mike Johnson' },
        expectedResponse: '2-4 hours',
        nextSteps: ['Order confirmation email sent', 'Vendor will contact you within 2-4 hours', 'Shipping details will be provided', 'Tracking information will be shared']
      },
      'Best Buy': {
        name: 'Best Buy',
        logo: "https://img.rocket.new/generatedImages/rocket_gen_img_11ad868de-1766607648821.png",
        logoAlt: 'Apple MacBook Air 13-inch in Midnight color with M3 chip, ultra-thin design and clean aesthetic',
        contact: { email: 'support@bestbuy.com', phone: '(888) 237-8289', representative: 'Sarah Davis' },
        expectedResponse: '1-2 hours',
        nextSteps: ['Order processed and confirmed', 'Geek Squad team notified for setup', 'Pickup or delivery coordination', 'Installation scheduling if needed']
      },
      'B&H Photo': {
        name: 'B&H Photo',
        logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1c5fb4c37-1769285478273.png",
        logoAlt: 'Apple MacBook Pro 14-inch with latest M4 chip, professional laptop with premium build quality',
        contact: { email: 'info@bhphotovideo.com', phone: '(800) 606-6969', representative: 'David Chen' },
        expectedResponse: '3-6 hours',
        nextSteps: ['Professional sales team notified', 'Product reservation confirmed', 'Shipping preparation initiated', 'Extended warranty options reviewed']
      }
    };
    return vendors?.[name] || vendors?.['Amazon'];
  };

  const selectedVendor = getVendorData(vendorName);

  useEffect(() => {
    const loadPaymentData = async () => {
      try {
        if (paymentIntentId) {
          const record = await getPaymentByIntentId(paymentIntentId);
          setPaymentRecord(record);
        }
      } catch (err) {
        console.error('Failed to load payment record:', err);
        // Non-critical — page still shows success
      } finally {
        setPageLoading(false);
        addToast('Payment confirmed! Your Stack is now active. 🎉', 'success', 5000);
      }
    };

    const loadTimer = setTimeout(loadPaymentData, 800);
    const confettiTimer = setTimeout(() => setShowConfetti(false), 5000);
    return () => { clearTimeout(loadTimer); clearTimeout(confettiTimer); };
  }, [paymentIntentId]);

  const transactionData = {
    id: paymentRecord?.payment_intent_id || paymentIntentId || 'TXN-DEMO123',
    amount: paymentRecord?.amount || 500,
    currency: (paymentRecord?.currency || 'usd')?.toUpperCase(),
    status: paymentRecord?.payment_status === 'succeeded' ? 'Completed' : (paymentRecord?.payment_status || 'Completed'),
    date: paymentRecord?.created_at ? new Date(paymentRecord.created_at)?.toLocaleDateString() : new Date()?.toLocaleDateString(),
    billingName: paymentRecord?.billing_name || '',
    billingEmail: paymentRecord?.billing_email || '',
    vendorName: paymentRecord?.vendor_name || vendorName,
  };

  const projectMilestones = [
    { id: 1, title: 'Order Processing', description: 'Vendor confirms order and begins preparation', expectedDate: new Date(Date.now() + 24 * 60 * 60 * 1000)?.toLocaleDateString(), status: 'pending', icon: 'Package' },
    { id: 2, title: 'Shipping & Logistics', description: 'Product shipped with tracking information', expectedDate: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000)?.toLocaleDateString(), status: 'upcoming', icon: 'Truck' },
    { id: 3, title: 'Delivery & Setup', description: 'Product delivered and set up if requested', expectedDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000)?.toLocaleDateString(), status: 'upcoming', icon: 'Home' },
    { id: 4, title: 'Stack Completion', description: 'Final verification and stack closure', expectedDate: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000)?.toLocaleDateString(), status: 'upcoming', icon: 'CheckCircle' }
  ];

  const handleContactVendor = () => navigate('/buyer-vendor-messaging-interface');
  const handleViewProjectDashboard = () => navigate(`/project-milestone-tracker?project=${projectId}`);
  const handleBackToProjects = () => navigate('/all-projects');

  if (pageLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <div className="flex-1 flex flex-col items-center justify-center gap-6 pt-32">
          <div className="w-20 h-20 rounded-2xl flex items-center justify-center shadow-xl" style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B, #E0528A)' }}>
            <svg className="animate-spin h-10 w-10 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          </div>
          <div className="text-center">
            <h2 className="text-xl font-bold text-gray-900 mb-1">Confirming Payment...</h2>
            <p className="text-gray-500 text-sm">Please wait while we verify your transaction</p>
          </div>
          <div className="flex gap-2">
            {[0, 1, 2]?.map((i) => (
              <div key={i} className="w-2 h-2 rounded-full animate-bounce" style={{ background: 'linear-gradient(135deg, #F58A54, #E0528A)', animationDelay: `${i * 0.15}s` }} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 relative">
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      {showConfetti && (
        <div className="fixed inset-0 pointer-events-none z-10">
          <div className="absolute top-20 left-1/4 text-4xl animate-bounce">🎉</div>
          <div className="absolute top-32 right-1/4 text-4xl animate-bounce delay-100">🎊</div>
          <div className="absolute top-40 left-1/3 text-3xl animate-bounce delay-200">✨</div>
          <div className="absolute top-28 right-1/3 text-3xl animate-bounce delay-300">🚀</div>
        </div>
      )}
      <Header />
      <main className="pt-32">
        {/* Gradient Hero Banner */}
        <div className="relative overflow-hidden py-14 px-4 sm:px-6 lg:px-8" style={{ background: 'linear-gradient(to right, #F58A54, #F06F6B, #E0528A)' }}>
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-white/5 blur-3xl" />
            <div className="absolute bottom-0 -left-10 w-56 h-56 rounded-full bg-white/5 blur-2xl" />
            {[
              { name: 'CheckCircle', top: '15%', left: '3%', rotate: '-10deg', opacity: 0.35 },
              { name: 'Star', top: '60%', left: '1%', rotate: '8deg', opacity: 0.3 },
              { name: 'Package', top: '15%', right: '3%', rotate: '12deg', opacity: 0.35 },
              { name: 'Zap', top: '60%', right: '1%', rotate: '-9deg', opacity: 0.3 },
            ]?.map((item, i) => (
              <div key={i} className="absolute" style={{ top: item?.top, left: item?.left, right: item?.right, opacity: item?.opacity, transform: `rotate(${item?.rotate})` }}>
                <div className="w-12 h-12 rounded-2xl bg-white/20 border border-white/30 flex items-center justify-center backdrop-blur-sm">
                  <Icon name={item?.name} size={22} color="white" />
                </div>
              </div>
            ))}
          </div>
          <div className="relative max-w-4xl mx-auto text-center">
            <Breadcrumb
              projectName="Stack"
              customPath={[
                { label: 'Stacks', path: '/all-projects', isClickable: true },
                { label: 'Checkout', path: null, isClickable: false },
                { label: 'Payment Confirmation', path: null, isClickable: false }
              ]}
            />
            <div className="flex justify-center mb-4">
              <div className="w-20 h-20 rounded-2xl bg-white/20 border-2 border-white/40 flex items-center justify-center shadow-xl backdrop-blur-sm">
                <Icon name="CheckCircle2" size={40} color="white" />
              </div>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">Payment Successful! 🎉</h1>
            <p className="text-white/85 text-lg mb-5">Your Stack has been launched successfully</p>
            <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm border border-white/25 rounded-full px-5 py-2">
              <Icon name="Zap" size={16} color="white" />
              <span className="text-white font-semibold text-sm">Stack #{projectId} is now active</span>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Transaction Summary */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B)' }}>
                  <Icon name="Receipt" size={16} className="text-white" />
                </div>
                Transaction Summary
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
              {[
                { label: 'Deposit Paid', value: `$${Number(transactionData?.amount)?.toLocaleString()}`, highlight: true },
                { label: 'Currency', value: transactionData?.currency, highlight: false },
                { label: 'Status', value: transactionData?.status, highlight: true },
                { label: 'Date', value: transactionData?.date, highlight: false },
              ]?.map((item, i) => (
                <div key={i} className={`rounded-xl p-4 ${item?.highlight ? 'text-white' : 'bg-gray-50'}`} style={item?.highlight ? { background: 'linear-gradient(135deg, #F58A54, #F06F6B)' } : {}}>
                  <p className={`text-xs mb-1 ${item?.highlight ? 'text-white/75' : 'text-gray-500'}`}>{item?.label}</p>
                  <p className={`font-bold text-base ${item?.highlight ? 'text-white' : 'text-gray-900'}`}>{item?.value}</p>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-100">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Payment ID:</span>
                <span className="font-mono text-gray-900 text-xs truncate max-w-[60%]">{transactionData?.id}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Vendor:</span>
                <span className="font-semibold text-gray-900">{transactionData?.vendorName}</span>
              </div>
              {transactionData?.billingName && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Billed To:</span>
                  <span className="font-semibold text-gray-900">{transactionData?.billingName}</span>
                </div>
              )}
              {transactionData?.billingEmail && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Email:</span>
                  <span className="font-semibold text-gray-900">{transactionData?.billingEmail}</span>
                </div>
              )}
            </div>
          </div>

          {/* Vendor Info */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #F06F6B, #E0528A)' }}>
                <Icon name="Store" size={16} className="text-white" />
              </div>
              Vendor Notified
            </h2>
            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl mb-4">
              <img src={selectedVendor?.logo} alt={selectedVendor?.logoAlt} className="w-14 h-14 rounded-xl object-cover border border-gray-100" />
              <div>
                <h3 className="font-bold text-gray-900">{selectedVendor?.name}</h3>
                <p className="text-sm text-gray-500">Expected response: {selectedVendor?.expectedResponse}</p>
                <p className="text-xs text-gray-400 mt-0.5">{selectedVendor?.contact?.email}</p>
              </div>
              <div className="ml-auto">
                <div className="flex items-center gap-1.5 bg-emerald-50 px-3 py-1.5 rounded-full">
                  <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  <span className="text-xs font-semibold text-emerald-700">Notified</span>
                </div>
              </div>
            </div>
            <div className="space-y-2">
              {selectedVendor?.nextSteps?.map((step, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-emerald-50 flex items-center justify-center flex-shrink-0">
                    <Icon name="Check" size={11} className="text-emerald-600" />
                  </div>
                  <span className="text-sm text-gray-700">{step}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Milestone Timeline */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
            <h2 className="text-lg font-bold text-gray-900 mb-5 flex items-center gap-2">
              <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #F58A54, #E0528A)' }}>
                <Icon name="Calendar" size={16} className="text-white" />
              </div>
              Project Milestones
            </h2>
            <div className="space-y-4">
              {projectMilestones?.map((milestone, i) => (
                <div key={milestone?.id} className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-gray-100">
                    <Icon name={milestone?.icon} size={18} className="text-gray-500" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-gray-900 text-sm">{milestone?.title}</h4>
                      <span className="text-xs text-gray-400">{milestone?.expectedDate}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">{milestone?.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <button
              onClick={handleViewProjectDashboard}
              className="flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-semibold text-sm text-white shadow-md hover:opacity-90 transition-all"
              style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B)' }}
            >
              <Icon name="BarChart2" size={16} />
              View Dashboard
            </button>
            <button
              onClick={handleContactVendor}
              className="flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-semibold text-sm border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <Icon name="MessageCircle" size={16} />
              Message Vendor
            </button>
            <button
              onClick={handleBackToProjects}
              className="flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-semibold text-sm border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <Icon name="Grid" size={16} />
              All Stacks
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PaymentConfirmation;